import React, { useState, useEffect, useRef } from 'react';
import './Stratégie-SEO.css';
import Header from '../Header/Header';
import Footer from '../footer/footer';
import { motion, AnimatePresence } from 'framer-motion';
import TopAnnouncementBar from '../TopAnnouncementBar/TopAnnouncementBar';
import WhatsAppButton from '../whatsapp/WhatsAppButton';
import { Link } from 'react-router-dom';
import { 
  FaRocket, 
  FaChartLine, 
  FaSearch, 
  FaShieldAlt,
  FaMobileAlt,
  FaLink,
  FaPenFancy,
  FaGlobe,
  FaArrowRight,
  FaCheckCircle,
  FaAward,
  FaClock,
  FaCode,
  FaTachometerAlt,
  FaUsers
} from 'react-icons/fa';
import { 
  SiGoogle, 
  SiSemrush 
} from 'react-icons/si';
import { MdAutoAwesome, MdOutlineSpeed, MdOutlineSecurity, MdAnalytics, MdIntegrationInstructions, MdSearch, MdTrendingUp } from 'react-icons/md';
import { GiAstronautHelmet, GiStarsStack, GiGalaxy, GiSpiralShell, GiCrystalBall, GiCyberEye, GiArtificialIntelligence, GiThunderSkull, GiBrain, GiRocketThruster, GiMagnifyingGlass } from 'react-icons/gi';

const StrategieSEO = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isCursorActive, setIsCursorActive] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const sectionRef = useRef(null);
  let cursorTimeout;

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
      setIsCursorActive(true);
      
      clearTimeout(cursorTimeout);
      cursorTimeout = setTimeout(() => setIsCursorActive(false), 100);
    };
    
    const handleScroll = () => {
      if (sectionRef.current) {
        const rect = sectionRef.current.getBoundingClientRect();
        const scrollPercent = Math.min(1, Math.max(0, -rect.top / (rect.height - window.innerHeight)));
        setScrollProgress(scrollPercent);
      }
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(cursorTimeout);
    };
  }, []);

  const solutionsData = [
    {
      title: "Audit SEO Complet",
      tagline: "Diagnostic 360°",
      description: "Analyse approfondie de votre site pour identifier les points forts et les axes d'amélioration avec un plan d'action priorisé.",
      features: ["Analyse technique", "Audit de contenu", "Backlinks", "Plan d'action"],
      icon: <GiMagnifyingGlass />,
      color: "#3B82F6",
      stat: "+150% de trafic en 6 mois"
    },
    {
      title: "Optimisation On-Page",
      tagline: "Maximisez votre potentiel",
      description: "Amélioration de tous les éléments visibles et invisibles de vos pages web pour booster votre classement.",
      features: ["Balises optimisées", "Mots-clés stratégiques", "Contenu enrichi", "Images optimisées"],
      icon: <FaPenFancy />,
      color: "#10B981",
      stat: "+85% de clics"
    },
    {
      title: "Stratégie de Contenu",
      tagline: "Attirez votre audience",
      description: "Création et optimisation de contenu pour cibler les bonnes requêtes et attirer du trafic qualifié.",
      features: ["Recherche mots-clés", "Plan éditorial SEO", "Optimisation articles", "Linking interne"],
      icon: <FaChartLine />,
      color: "#8B5CF6",
      stat: "10x plus de trafic"
    },
    {
      title: "Netlinking Premium",
      tagline: "Autorité & confiance",
      description: "Acquisition de liens de qualité pour améliorer l'autorité et le classement de votre site sur Google.",
      features: ["Backlinks qualité", "Contenu lienable", "Diversification", "Suivi performances"],
      icon: <FaLink />,
      color: "#F59E0B",
      stat: "+40 points d'autorité"
    },
    {
      title: "SEO Local",
      tagline: "Dominez votre zone",
      description: "Optimisation pour les recherches géolocalisées et amélioration de votre visibilité locale.",
      features: ["Google My Business", "Citations locales", "Avis clients", "Classements locaux"],
      icon: <FaGlobe />,
      color: "#EC4899",
      stat: "+300% de visibilité locale"
    },
    {
      title: "Suivi & Reporting",
      tagline: "Pilotez votre succès",
      description: "Surveillance continue des performances et ajustement de la stratégie pour maximiser votre ROI.",
      features: ["Positions suivies", "Analyse trafic", "Reporting mensuel", "Recommandations"],
      icon: <FaRocket />,
      color: "#06B6D4",
      stat: "ROI mesurable"
    }
  ];

  const benefits = [
    { icon: <FaChartLine />, title: "Résultats Mesurables", description: "KPI précis et reporting transparent", color: "#3B82F6" },
    { icon: <MdSearch />, title: "Approche Holistique", description: "SEO technique, on-page & off-page", color: "#10B981" },
    { icon: <FaShieldAlt />, title: "Conformité Google", description: "Respect des guidelines", color: "#8B5CF6" },
    { icon: <FaMobileAlt />, title: "UX optimisée", description: "Réduction du taux de rebond", color: "#F59E0B" },
    { icon: <MdTrendingUp />, title: "Croissance durable", description: "Résultats dans la durée", color: "#EC4899" },
    { icon: <FaUsers />, title: "Trafic qualifié", description: "Visiteurs prêts à acheter", color: "#06B6D4" }
  ];

  const statsData = [
    { value: "+300%", label: "trafic organique", icon: <FaRocket /> },
    { value: "Top 3", label: "positions clés", icon: <FaTachometerAlt /> },
    { value: "95%", label: "satisfaction", icon: <FaAward /> },
    { value: "100+", label: "mots-clés", icon: <MdSearch /> }
  ];

  const technologies = [
    { icon: <SiGoogle />, name: "Google Analytics", color: "#4285F4" },
    { icon: <SiGoogle />, name: "Search Console", color: "#34A853" },
    { icon: <SiSemrush />, name: "SEMrush", color: "#FF6B6B" },
    { icon: <FaSearch />, name: "Ahrefs", color: "#000000" },
    { icon: <FaChartLine />, name: "Data Studio", color: "#FBBC05" },
    { icon: <FaMobileAlt />, name: "PageSpeed", color: "#EA4335" }
  ];

  const getPreviewBadgeText = () => {
    switch(activeTab) {
      case 0: return "Audit complet 360°";
      case 1: return "Optimisation avancée";
      case 2: return "Stratégie content";
      case 3: return "Netlinking premium";
      case 4: return "SEO local expert";
      case 5: return "Reporting sur-mesure";
      default: return "Solution SEO";
    }
  };

  return (
    <div className="seo-container" ref={sectionRef}>
      <WhatsAppButton />
      <TopAnnouncementBar />
      <Header />
      
      {/* Cursor Glow */}
      <motion.div 
        className={`seo-cursor-glow ${isCursorActive ? 'active' : ''}`}
        animate={{
          x: mousePosition.x - 300,
          y: mousePosition.y - 300,
        }}
        transition={{ type: "spring", damping: 30, stiffness: 200, mass: 0.5 }}
      />

      {/* Particles */}
      <div className="seo-particles">
        {[...Array(60)].map((_, i) => (
          <div key={i} className="seo-particle" style={{
            left: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 20}s`,
            animationDuration: `${20 + Math.random() * 30}s`,
            width: `${1.5 + Math.random() * 3}px`,
            height: `${1.5 + Math.random() * 3}px`,
          }} />
        ))}
      </div>

      {/* Animated Orbs */}
      <div className="seo-orbs">
        <div className="seo-orb seo-orb-1" style={{ transform: `translate(${scrollProgress * 100}px, ${scrollProgress * 50}px)` }} />
        <div className="seo-orb seo-orb-2" style={{ transform: `translate(-${scrollProgress * 80}px, -${scrollProgress * 60}px)` }} />
        <div className="seo-orb seo-orb-3" style={{ transform: `translate(${scrollProgress * 60}px, -${scrollProgress * 40}px)` }} />
        <div className="seo-orb seo-orb-4" style={{ transform: `translate(-${scrollProgress * 70}px, ${scrollProgress * 70}px)` }} />
      </div>

      {/* Floating Elements */}
      <div className="seo-floating">
        <GiGalaxy className="seo-float seo-float-1" />
        <GiSpiralShell className="seo-float seo-float-2" />
        <GiAstronautHelmet className="seo-float seo-float-3" />
        <GiStarsStack className="seo-float seo-float-4" />
        <GiCrystalBall className="seo-float seo-float-5" />
        <GiCyberEye className="seo-float seo-float-6" />
        <GiArtificialIntelligence className="seo-float seo-float-7" />
        <GiThunderSkull className="seo-float seo-float-8" />
        <GiBrain className="seo-float seo-float-9" />
        <GiRocketThruster className="seo-float seo-float-10" />
        <GiMagnifyingGlass className="seo-float seo-float-11" />
      </div>
      
      {/* ========== HERO BANNER ========== */}
      <section className="seo-hero">
        <div className="seo-hero-overlay"></div>
        <div className="seo-hero-inner">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="seo-hero-badge"
          >
            <GiMagnifyingGlass />
            <span>SEO & Visibility Excellence</span>
            <GiStarsStack />
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="seo-hero-title"
          >
            Stratégie SEO sur-mesure<br />
            <span className="seo-gradient-text">pour dominer Google</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="seo-hero-subtitle"
          >
            Optimisation technique et stratégique pour améliorer durablement votre visibilité 
            sur les moteurs de recherche et générer plus de trafic qualifié.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="seo-hero-buttons"
          >
            <Link to="/reservation-appel">
              <button className="seo-btn seo-btn-primary">
                <MdAutoAwesome /> Débuter mon projet <FaArrowRight />
              </button>
            </Link>
            <Link to="/realisations">
              <button className="seo-btn seo-btn-secondary">
                <FaCode /> Voir nos résultats
              </button>
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="seo-hero-stats"
          >
            {statsData.map((stat, idx) => (
              <div key={idx} className="seo-stat-card">
                <div className="seo-stat-icon">{stat.icon}</div>
                <div className="seo-stat-value">{stat.value}</div>
                <div className="seo-stat-label">{stat.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ========== BENEFITS SECTION ========== */}
      <section className="seo-benefits">
        <div className="seo-container-inner">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="seo-section-header"
          >
            <div className="seo-badge">Pourquoi nous</div>
            <h2>Une approche SEO <span className="seo-gradient-text">unique et performante</span></h2>
            <p>Une méthodologie éprouvée pour des résultats durables et mesurables</p>
          </motion.div>

          <div className="seo-benefits-grid">
            {benefits.map((benefit, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.05 }}
                whileHover={{ y: -8 }}
                className="seo-benefit-card"
                style={{ '--seo-card-color': benefit.color }}
              >
                <div className="seo-benefit-icon" style={{ color: benefit.color }}>{benefit.icon}</div>
                <h3>{benefit.title}</h3>
                <p>{benefit.description}</p>
                <div className="seo-benefit-glow" style={{ background: `radial-gradient(circle, ${benefit.color}20, transparent)` }} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== SOLUTIONS SECTION - TABS ========== */}
      <section className="seo-solutions">
        <div className="seo-container-inner">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="seo-section-header"
          >
            <div className="seo-badge">Nos services</div>
            <h2>Services SEO <span className="seo-gradient-text">complets</span></h2>
            <p>Des solutions adaptées à chaque besoin et chaque étape de votre croissance</p>
          </motion.div>

          {/* Tabs navigation */}
          <div className="seo-tabs-nav">
            {solutionsData.map((sol, idx) => (
              <button
                key={idx}
                className={`seo-tab-btn ${activeTab === idx ? 'active' : ''}`}
                onClick={() => setActiveTab(idx)}
                style={{ '--seo-tab-color': sol.color }}
              >
                <span className="seo-tab-icon">{sol.icon}</span>
                <span className="seo-tab-title">{sol.title}</span>
              </button>
            ))}
          </div>

          {/* Tab content */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
              className="seo-tab-content"
            >
              <div className="seo-tab-left">
                <div className="seo-solution-tagline" style={{ color: solutionsData[activeTab].color }}>
                  {solutionsData[activeTab].tagline}
                </div>
                <h3>{solutionsData[activeTab].title}</h3>
                <p>{solutionsData[activeTab].description}</p>
                <div className="seo-features-list">
                  {solutionsData[activeTab].features.map((feat, i) => (
                    <div key={i} className="seo-feature-item">
                      <FaCheckCircle style={{ color: solutionsData[activeTab].color }} />
                      <span>{feat}</span>
                    </div>
                  ))}
                </div>
                <div className="seo-solution-stat" style={{ background: `${solutionsData[activeTab].color}15`, borderLeftColor: solutionsData[activeTab].color }}>
                  <FaRocket />
                  <span><strong>{solutionsData[activeTab].stat}</strong></span>
                </div>
              </div>
              <div className="seo-tab-right">
                <div className="seo-preview-card" style={{ background: `linear-gradient(135deg, ${solutionsData[activeTab].color}10, rgba(10,17,40,0.4))` }}>
                  <div className="seo-preview-icon" style={{ color: solutionsData[activeTab].color }}>
                    {solutionsData[activeTab].icon}
                  </div>
                  <div className="seo-preview-badge">
                    {getPreviewBadgeText()}
                  </div>
                  <div className="seo-preview-stats">
                    <span>📊 Data-driven</span>
                    <span>🎯 Ciblé</span>
                    <span>📈 ROI prouvé</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* ========== TECHNOLOGIES SECTION ========== */}
      <section className="seo-tech">
        <div className="seo-container-inner">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="seo-section-header"
          >
            <div className="seo-badge">Stack technique</div>
            <h2>Outils et <span className="seo-gradient-text">technologies SEO</span></h2>
            <p>Nous utilisons les meilleurs outils du marché pour maximiser vos résultats</p>
          </motion.div>

          <div className="seo-tech-grid">
            {technologies.map((tech, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.05 }}
                whileHover={{ y: -5 }}
                className="seo-tech-card"
              >
                <div className="seo-tech-icon" style={{ color: tech.color }}>{tech.icon}</div>
                <h4>{tech.name}</h4>
                <div className="seo-tech-bar">
                  <div className="seo-tech-progress" style={{ backgroundColor: tech.color }} />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== FINAL CTA ========== */}
      <section className="seo-final-cta">
        <div className="seo-cta-pattern"></div>
        <div className="seo-cta-inner">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="seo-cta-icon"
          >
            <GiRocketThruster />
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            Prêt à <span className="seo-gradient-text">dominer</span> les résultats de recherche ?
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            Obtenez un audit SEO gratuit de votre site et découvrez comment améliorer votre visibilité.
          </motion.p>
          <Link to="/reservation-appel">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              className="seo-cta-button"
            >
              <MdAutoAwesome /> Demander un audit gratuit <FaArrowRight />
            </motion.button>
          </Link>
          <div className="seo-cta-features">
            <div><FaCheckCircle /> Réponse sous 24h</div>
            <div><FaShieldAlt /> Garantie satisfait</div>
            <div><FaUsers /> Support dédié</div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default StrategieSEO;